import {
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Avatar,
  Tooltip,
  Box,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { colorEstado, labelEstado } from "./subscriptions/utils";
import { STAGE_COLORS, stageColor, WHATSAPP_GREEN } from "./theme/brand";
import { canalLabel } from "./pipelineUtils";

const ETAPA_COLORS = STAGE_COLORS;

export { ETAPA_COLORS };

export default function LeadPipelineCard({
  lead,
  vendedor,
  etapa,
  subInfo,
  diasEntreSeguimiento,
  isDragging,
  onDragStart,
  onDragEnd,
  onOpenWA,
  onOpenMenu,
  onContactar,
  onAgendar,
  onMarcarInscrito,
  ...restProps
}) {
  const diasSinContacto = () => {
    if (!lead.ultimoContacto) return null;
    return Math.floor((Date.now() - new Date(lead.ultimoContacto).getTime()) / 86400000);
  };

  const textoTiempo = () => {
    if (!lead.ultimoContacto) return "Sin contactar";
    const dias = diasSinContacto();
    if (dias === 0) return "Contactado hoy";
    if (dias === 1) return "Hace 1 día";
    return `Hace ${dias} días`;
  };

  const dias = diasSinContacto();
  const enRiesgo = dias !== null && dias >= 3 && etapa !== "inscrito" && etapa !== "perdido";

  return (
    <Card
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      variant="outlined"
      {...restProps}
      sx={{
        borderLeft: 3,
        borderLeftColor: enRiesgo ? "error.main" : "transparent",
        transition: "box-shadow 0.15s, opacity 0.15s, transform 0.1s",
        "&:hover": { boxShadow: 3, transform: "translateY(-1px)" },
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        opacity: isDragging ? 0.4 : 1,
        userSelect: "none",
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        {vendedor && <Tooltip title={`Asignado a ${vendedor.nombre}`}><Avatar aria-label={`Asignado a ${vendedor.nombre}`} sx={{ bgcolor: vendedor.color || '#555', color: '#fff', width: 24, height: 24, fontSize: 12, mb: 0.5 }}>{vendedor.nombre?.[0]?.toUpperCase()}</Avatar></Tooltip>}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 28, height: 28, fontSize: 14, fontWeight: 700, bgcolor: stageColor(etapa) }}>
              {lead.nombre?.charAt(0)?.toUpperCase() || "?"}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 120 }}>
                {lead.nombre || "Sin nombre"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120, display: "block" }}>
                {lead.cursoInteres || "—"}
                {Number(lead.montoEstimado) > 0 && ` · ${Number(lead.montoEstimado).toLocaleString()}`}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0}>
            {lead.telefono && (
              <Tooltip title="Enviar WhatsApp">
                <IconButton
                  size="small"
                  sx={{ color: WHATSAPP_GREEN }}
                  aria-label="Enviar WhatsApp"
                  onClick={(e) => { e.stopPropagation(); onOpenWA(lead); }}
                  onDragStart={(e) => e.stopPropagation()}
                  draggable={false}
                >
                  <WhatsAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              aria-label="Más acciones"
              onClick={(e) => { e.stopPropagation(); onOpenMenu(e, lead.id); }}
              onDragStart={(e) => e.stopPropagation()}
              draggable={false}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Meta info */}
        <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" gap={0.5}>
          {lead.canal && (
            <Chip
              icon={lead.canal === "whatsapp" ? <WhatsAppIcon /> : lead.canal === "presencial" ? <PersonIcon /> : <PhoneIcon />}
              label={canalLabel(lead.canal)}
              size="small"
              variant="outlined"
              sx={{ fontSize: 11, height: 22 }}
            />
          )}
          {lead.edad && (
            <Chip label={`${lead.edad} años`} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
          )}
        </Stack>

        {/* Último contacto */}
        <Stack direction="row" spacing={0.5} mt={0.5} alignItems="center">
          <AccessTimeIcon sx={{ fontSize: 12, color: enRiesgo ? "error.main" : "text.disabled" }} />
          <Typography
            variant="caption"
            sx={{
              color: enRiesgo ? "error.main" : "text.secondary",
              fontWeight: enRiesgo ? 700 : 400,
              fontSize: 11,
            }}
          >
            {textoTiempo()}
          </Typography>
        </Stack>

        {/* Próxima visita sugerida */}
        {lead.ultimoContacto && diasEntreSeguimiento > 0 && etapa !== "inscrito" && etapa !== "perdido" && (() => {
          const fecha = new Date(new Date(lead.ultimoContacto).getTime() + diasEntreSeguimiento * 86400000);
          const hoyD = new Date(); hoyD.setHours(0,0,0,0);
          const diff = Math.ceil((fecha.getTime() - hoyD.getTime()) / 86400000);
          const color = diff < 0 ? "error" : diff <= 2 ? "warning" : "default";
          const txt = diff < 0 ? `vencida hace ${Math.abs(diff)} d` : diff === 0 ? "hoy" : `en ${diff} d`;
          return (
            <Chip
              icon={<EventAvailableIcon sx={{ fontSize: "14px !important" }} />}
              label={`Revisita ${txt}`}
              size="small"
              color={color}
              variant="outlined"
              sx={{ mt: 0.5, fontSize: 10, height: 20 }}
            />
          );
        })()}

        {/* Suscripción badge */}
        {subInfo && (
          <Chip
            icon={<AutorenewIcon sx={{ fontSize: "14px !important" }} />}
            label={`${labelEstado(subInfo.estado)} (${subInfo.dias}d)`}
            size="small"
            sx={{
              mt: 0.5,
              fontSize: 10,
              height: 20,
              bgcolor: colorEstado(subInfo.estado),
              color: "#fff",
              fontWeight: 700,
              "& .MuiChip-icon": { color: "#fff" },
            }}
          />
        )}

        {/* Quick actions */}
        {etapa !== "inscrito" && etapa !== "perdido" && (
          <Stack direction="row" spacing={0.5} mt={0.5} useFlexGap flexWrap="wrap">
            <Tooltip title="Registrar contacto">
              <Chip
                label="Registrar contacto"
                size="small"
                variant="outlined"
                clickable
                onClick={(e) => { e.stopPropagation(); onContactar(lead.id); }}
                onDragStart={(e) => e.stopPropagation()}
                draggable={false}
                sx={{ fontSize: 10, height: 20 }}
              />
            </Tooltip>
            <Tooltip title="Agendar seguimiento para mañana">
              <Chip
                icon={<EventNoteIcon sx={{ fontSize: "14px !important" }} />}
                label="Agendar"
                size="small"
                variant="outlined"
                clickable
                onClick={(e) => { e.stopPropagation(); onAgendar(lead.id); }}
                onDragStart={(e) => e.stopPropagation()}
                draggable={false}
                sx={{ fontSize: 10, height: 20 }}
              />
            </Tooltip>
            <Tooltip title="Registrar venta">
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                label="Venta"
                size="small"
                variant="outlined"
                color="success"
                clickable
                onClick={(e) => { e.stopPropagation(); onMarcarInscrito(lead.id); }}
                onDragStart={(e) => e.stopPropagation()}
                draggable={false}
                sx={{ fontSize: 10, height: 20 }}
              />
            </Tooltip>
          </Stack>
        )}

        {lead.observaciones && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block", lineHeight: 1.3 }}
            noWrap
          >
            {lead.observaciones}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
